import { AlertTriangle, CheckCircle2, Route } from 'lucide-react';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { cn } from '../../utils/cn.js';
import type { EntitySummary } from './entity-summaries-data.js';
import { TONE_CLASS } from './entity-tone-classes.js';

/** One entity as a selectable tile; the selected one shows its detail below. */
export function SummaryCard({
  entity,
  active,
  onSelect,
}: {
  entity: EntitySummary;
  active: boolean;
  onSelect: () => void;
}) {
  const Icon = entity.icon;
  const tone = TONE_CLASS[entity.tone];
  return (
    <Button
      variant="ghost"
      onClick={onSelect}
      aria-pressed={active}
      aria-label={entity.name}
      className={cn(
        'h-auto min-h-[132px] flex-col items-stretch justify-start gap-0 rounded-md border p-4 text-left whitespace-normal',
        tone.fill,
        tone.border,
        active && 'ring-2 ring-ring',
      )}
    >
      <span className="flex items-center gap-3">
        <span className={cn('rounded-md border p-2', tone.chip)}>
          <Icon size={18} />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-heading">{entity.name}</span>
          <span className={cn('block text-[10px] tracking-widest uppercase', tone.text)}>{entity.category}</span>
        </span>
      </span>
      <span className="mt-3 line-clamp-3 text-xs leading-relaxed font-normal text-body">{entity.summary}</span>
    </Button>
  );
}

function DetailList({
  title,
  icon,
  items,
  titleClass,
}: {
  title: string;
  icon: React.ReactNode;
  items: readonly string[];
  titleClass: string;
}) {
  return (
    <div className="rounded-md border border-card-border bg-surface p-4">
      <div className={cn('flex items-center gap-2 text-sm font-semibold', titleClass)}>
        {icon} {title}
      </div>
      <ul className="mt-3 space-y-2 text-xs leading-relaxed text-body">
        {items.map((item) => (
          <li key={item}>- {item}</li>
        ))}
      </ul>
    </div>
  );
}

/** The selected entity's boundary: what it owns, what it is not for, what it connects to. */
export function DetailPanel({ entity }: { entity: EntitySummary }) {
  const Icon = entity.icon;
  const tone = TONE_CLASS[entity.tone];
  return (
    <Card className={cn('border p-5', tone.fill, tone.border)}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={cn('rounded-md border p-2.5', tone.chip)}>
            <Icon size={20} />
          </span>
          <div>
            <h2 className="text-xl font-semibold text-heading">{entity.name}</h2>
            <p className={cn('text-xs tracking-widest uppercase', tone.text)}>{entity.category}</p>
          </div>
        </div>
        <Badge variant="neutral">Entity boundary</Badge>
      </div>
      <p className="mt-4 max-w-4xl text-sm leading-6 text-body">{entity.summary}</p>

      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <DetailList title="Owns" icon={<CheckCircle2 size={16} />} items={entity.owns} titleClass="text-success-text" />
        <DetailList
          title="Not for"
          icon={<AlertTriangle size={16} />}
          items={entity.notFor}
          titleClass="text-danger-text"
        />
        <DetailList
          title="Connects"
          icon={<Route size={16} />}
          items={entity.relationships}
          titleClass="text-info-text"
        />
      </div>
    </Card>
  );
}
